import type { PortfolioProject } from '../types/portfolio';

export const portfolioProjects: PortfolioProject[] = [
  {
    id: 'buy-local-bay-area',
    name: 'BuyLocal Bay Area',
    url: 'https://buylocalbayarea.com',
    displayUrl: 'buylocalbayarea.com',
    category: 'Local commerce platform',
    summary:
      'A live Bay Area commerce platform with a curated directory and city, neighborhood, and category discovery.',
    contribution: 'Consltr led product strategy, marketplace design, and full-stack delivery.',
    outcome:
      "Live, with Founder's Featured Listings at $249 for the first year and $500 per year on renewal.",
    status: 'Live',
    technology: [
      { layer: 'Frontend', tools: 'Next.js 14, React, TypeScript, Tailwind CSS' },
      { layer: 'Backend', tools: 'Node.js 20, Express, PostgreSQL 15' },
      { layer: 'Infrastructure', tools: 'Google Cloud Run, Cloud SQL, Docker' },
    ],
    systemDesign:
      'A multi-tenant, per-city platform with separate frontend and API hosts. Containerized services run in-region on Cloud Run, backed by Cloud SQL and connected to CRM and search systems.',
    metric: {
      value: '$249',
      label: 'first-year Founder listing',
    },
    services: ['Product strategy', 'Marketplace', 'Design & engineering'],
    mark: 'BL',
    accent: '#f26a3d',
    surface: '#f3eadb',
    foreground: '#20231f',
  },
  {
    id: 'salsa',
    name: 'Salsa',
    url: 'https://salsas.app',
    displayUrl: 'salsas.app',
    category: 'AI-native workspace',
    summary:
      'A collaborative workspace for chat, meetings, tracked work, living notes, and verified agent execution.',
    contribution:
      'Consltr shaped product strategy, architecture, design systems, and full-stack delivery.',
    outcome: 'In private beta for macOS, with a five-stage verified execution loop.',
    status: 'Private beta',
    technology: [
      { layer: 'Core & backend', tools: 'Rust, Axum, Tokio, SQLite WAL' },
      { layer: 'Interface', tools: 'Leptos SSR and hydration, TypeScript, macOS' },
      { layer: 'Knowledge & agents', tools: 'LanceDB, SurrealDB, GraphRAG, isolated Linux VMs' },
    ],
    systemDesign:
      'An event-sourced, CQRS operating system: an append-only event log feeds fast read models, a Rust web layer, persistent GraphRAG memory, tiered model routing, and signed execution inside isolated virtual machines.',
    metric: {
      value: '5 stages',
      label: 'from plan through verified result',
    },
    mark: 'S',
    accent: '#ff5a45',
    surface: '#151313',
    foreground: '#fff8f4',
    services: ['Product architecture', 'Design systems', 'Applied AI'],
  },
  {
    id: 'onzoe',
    name: 'Onzoe',
    url: 'https://onzoe.com',
    displayUrl: 'onzoe.com',
    category: 'Website voice agents',
    summary:
      'Website voice agents that capture leads through a one-widget install and turn conversations into useful activity.',
    contribution: 'Consltr led voice-AI product design, SaaS engineering, and systems integration.',
    outcome: 'Live with a 14-day free trial.',
    status: 'Live',
    technology: [
      { layer: 'Frontend & widget', tools: 'React, TypeScript, Vite, embeddable JavaScript' },
      { layer: 'Voice backend', tools: 'Rust, Axum, Tokio, Node.js services' },
      { layer: 'Data & edge', tools: 'PostgreSQL, Redis, Envoy, Docker' },
    ],
    systemDesign:
      'Envoy terminates TLS and routes authentication, marketing, and voice traffic to independent services. The Rust voice core orchestrates speech-to-text, text-to-speech, and voice agents across multiple AI providers.',
    metric: {
      value: '14 days',
      label: 'free trial',
    },
    services: ['Voice AI', 'SaaS platform', 'Systems integration'],
    mark: 'O',
    accent: '#8b5cf6',
    surface: '#171225',
    foreground: '#f7f3ff',
  },
  {
    id: 'aura-wealth-trainer',
    name: 'Aura Wealth Trainer',
    url: 'https://auratrainer.app',
    displayUrl: 'auratrainer.app',
    category: 'Voice coaching product',
    summary:
      'A voice-led wealth practice with lessons, spoken exercises, reflections, progress tracking, and AI coaching conversations.',
    contribution:
      'Consltr led product strategy, the native voice experience, and applied-AI delivery.',
    outcome: 'Live as a guided 21-day practice.',
    status: 'Live',
    technology: [
      { layer: 'Native apps', tools: 'SwiftUI, The Composable Architecture, SwiftData' },
      { layer: 'Backend', tools: 'Node.js, Express, WebSockets, Rust TTS sidecar' },
      { layer: 'Voice & data', tools: 'PostgreSQL, Redis, LiveKit, Cartesia, ElevenLabs, OpenAI' },
    ],
    systemDesign:
      'The native app uses reducer-driven state and local persistence while a WebSocket control plane streams coaching sessions. A private Rust sidecar handles cached speech synthesis behind the Node.js API and automatic provider fallback.',
    metric: {
      value: '21 days',
      label: 'guided voice practice',
    },
    services: ['Product strategy', 'Voice experience', 'Applied AI'],
    mark: 'A',
    accent: '#d4af37',
    surface: '#0f172a',
    foreground: '#fffdf8',
  },
  {
    id: 'yummstr',
    name: 'Yummstr',
    url: 'https://yummstr.com',
    displayUrl: 'yummstr.com',
    category: 'Social impact product',
    summary:
      'A social food platform where every shared photo helps provide a meal to someone in need.',
    contribution:
      'Consltr shaped the product, designed the community experience, and built the web platform and production API.',
    outcome:
      'The web experience and API are live; native iOS and Android apps remain in development.',
    status: 'Web live',
    technology: [
      { layer: 'Backend', tools: 'Zig 0.16, std.http, libpq, PostgreSQL, Redis' },
      { layer: 'Clients', tools: 'React and Vite, SwiftUI, Kotlin and Jetpack Compose' },
      { layer: 'Infrastructure', tools: 'Google Cloud Run, Cloud SQL, Firebase Hosting' },
    ],
    systemDesign:
      'A native Zig REST API preserves one contract for web, iOS, and Android clients. It uses a multithreaded server, pooled PostgreSQL access, optional cache-aside Redis, structured telemetry, and independent client deployments.',
    services: ['Product design', 'Community platform', 'Web application'],
    mark: 'Y',
    accent: '#ff6b35',
    surface: '#fff2e8',
    foreground: '#2b1710',
  },
  {
    id: 'the-genie',
    name: 'The Genie',
    url: 'https://www.thegeniemusic.com',
    displayUrl: 'thegeniemusic.com',
    category: 'Artist brand & website',
    summary:
      'A multilingual digital home for the San Francisco artist, his music, live-looping performances, and immersive Crystal Mirror Space.',
    contribution:
      'Consltr led the 2026 brand and website overhaul, including strategy, identity, copy, and the Wix build.',
    outcome:
      'Live in English, French, Spanish, and Japanese after a reversible legacy-site migration.',
    status: 'Live in 4 languages',
    technology: [
      { layer: 'Experience', tools: 'Wix, semantic HTML, CSS, JavaScript' },
      { layer: 'Content system', tools: 'Shared translation source, localized static generation' },
      { layer: 'Discovery', tools: 'SemanticDOM SSG, JSON-LD, llms.txt, multilingual sitemap' },
    ],
    systemDesign:
      'One shared content source generates crawlable English, French, Spanish, and Japanese pages plus matching AI-discovery artifacts. The production Wix app and preserved pre-migration capture keep publication and rollback reversible.',
    metric: {
      value: '4 languages',
      label: 'on one responsive site',
    },
    services: ['Brand strategy', 'Multilingual web', 'Content system'],
    mark: 'G',
    accent: '#00d4a6',
    surface: '#071a19',
    foreground: '#edfffb',
  },
];
